import {
    Alert, Box, Button, Chip, CircularProgress, Divider, List, ListItem,
    ListItemIcon, ListItemText, Paper, Typography
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import themeColors from "../theme";
import HeaderBar from "../components/HeaderBar";
import { createCheckoutSession, fetchSubscription } from "../api/subscriptionApi";
import { formatPrice } from "../utils/subscription";

const PLAN_FEATURES = [
    "CME credit tracking across every state you’re licensed in",
    "Store your license information and important documents in a secure online vault",
    "CME fund tracking for reimbursable purchases",
    "Clinical guideline updates in emergency medicine",
    "Online marketplace for CME purchases",
];

export default function SubscribePage() {
    const { teal, darkTeal, lightGray } = themeColors;
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        try {
            setData(await fetchSubscription());
        } catch {
            setError("Could not load your subscription details. Please refresh and try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSubscribe = async () => {
        setStarting(true);
        setError(null);
        try {
            const { url } = await createCheckoutSession();
            window.location.assign(url);
        } catch (err) {
            setError(err.response?.data?.error || "Could not open Stripe checkout. Please try again.");
            setStarting(false);
        }
    };

    if (loading) {
        return (
            <>
                <HeaderBar limitedNavigation />
                <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                    <CircularProgress sx={{ color: teal }} />
                </Box>
            </>
        );
    }

    const plan = data?.plan;
    const subscription = data?.subscription;
    const priceLabel = plan ? formatPrice(plan.priceCents, plan.currency) : "$99";
    const promoCode = plan?.firstYearPromoCode || "FREEYEAR";

    if (subscription?.active) {
        return (
            <>
                <HeaderBar limitedNavigation />
                <Box sx={{ backgroundColor: lightGray, minHeight: "calc(100vh - 64px)", py: 5, px: 2 }}>
                    <Paper elevation={1} sx={{ p: 4, borderRadius: 2, textAlign: "center", maxWidth: 560, mx: "auto" }}>
                        <CheckCircleOutlineIcon sx={{ color: teal, fontSize: 52, mb: 1 }} />
                        <Typography variant="h5" fontWeight={700} sx={{ color: "#1D3557", mb: 1 }}>
                            Your subscription is active
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#5A6C7D", mb: 3 }}>
                            Your Stripe payment method is on file and your OnCallCME access is ready.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate("/dashboard")}
                            sx={{ backgroundColor: teal, textTransform: "none", fontWeight: 600, "&:hover": { backgroundColor: darkTeal } }}
                        >
                            Continue to Dashboard
                        </Button>
                    </Paper>
                </Box>
            </>
        );
    }

    return (
        <>
            <HeaderBar limitedNavigation />
            <Box sx={{ backgroundColor: lightGray, minHeight: "calc(100vh - 64px)", py: 5, px: 2 }}>
                <Box sx={{ maxWidth: 560, mx: "auto" }}>
                    <Typography variant="h4" fontWeight={700} sx={{ color: "#1D3557", mb: 1 }}>
                        Complete your subscription
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#5A6C7D", mb: 4 }}>
                        Add a payment method with Stripe to finish setting up your account.
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                    {data && !data.billingEnabled && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Billing isn't configured on this server yet. Please contact support.
                        </Alert>
                    )}

                    <Paper elevation={1} sx={{ p: 4, borderRadius: 2, mb: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1 }}>
                            <Typography variant="h5" fontWeight={700} sx={{ color: "#1D3557" }}>
                                Standard
                            </Typography>
                            <Chip label="First year free" color="success" size="small" sx={{ fontWeight: 600 }} />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 2 }}>
                            <Typography variant="h3" fontWeight={700} sx={{ color: teal }}>
                                {priceLabel}
                            </Typography>
                            <Typography variant="body1" sx={{ color: "#5A6C7D" }}>per year</Typography>
                        </Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Code <strong>{promoCode}</strong> is applied at Stripe checkout: $0 today, then {priceLabel} per year starting next year.
                        </Alert>
                        <Divider sx={{ mb: 2 }} />
                        <List dense disablePadding>
                            {PLAN_FEATURES.map((feature) => (
                                <ListItem key={feature} disableGutters sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <CheckCircleOutlineIcon sx={{ color: teal, fontSize: 20 }} />
                                    </ListItemIcon>
                                    <ListItemText primary={feature} primaryTypographyProps={{ variant: "body2", sx: { color: "#1D3557" } }} />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>

                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        endIcon={<OpenInNewIcon />}
                        onClick={handleSubscribe}
                        disabled={starting || !data?.billingEnabled}
                        sx={{ backgroundColor: teal, textTransform: "none", fontWeight: 600, py: 1.5, "&:hover": { backgroundColor: darkTeal } }}
                    >
                        {starting ? "Opening Stripe checkout..." : "Add payment method with Stripe"}
                    </Button>
                    <Typography variant="caption" sx={{ color: "#5A6C7D", display: "block", textAlign: "center", mt: 2 }}>
                        Secure checkout by Stripe. Confirm that checkout shows $0 due today before subscribing.
                    </Typography>
                </Box>
            </Box>
        </>
    );
}
